import { startsWith } from 'lodash-es';
import { v4 as generateUUID } from 'uuid';
import * as ClickedConverter from './SingleFilterConverter/ClickedConverter';
import * as ErrorConverter from './SingleFilterConverter/ErrorConverter';
import * as BrowserConverter from './SingleFilterConverter/BrowserConverter';
import * as DateRangeConverter from './SingleFilterConverter/DateRangeConverter';
import * as DeviceTypeConverter from './SingleFilterConverter/DeviceTypeConverter';
import * as FeedbackConverter from './SingleFilterConverter/FeedbackConverter';
import * as ExperimentConverter from './SingleFilterConverter/ExperimentConverter';
import * as LargeStringListConverter from './SingleFilterConverter/GenericConverter/LargeStringListConverter';
import * as NewReturningUserConverter from './SingleFilterConverter/NewReturningUserConverter';
import * as NPSConverter from './SingleFilterConverter/NPSConverter';
import * as OperatingSystemConverter from './SingleFilterConverter/OperatingSystemConverter';
import * as NumberConverter from './SingleFilterConverter/GenericConverter/RadioNumberConverter';
import * as RageClickConverter from './SingleFilterConverter/RageClickConverter';
import * as SessionDurationConverter from './SingleFilterConverter/SessionDurationConverter';
import * as URLConverter from './SingleFilterConverter/URLConverter';
import * as UserIdConverter from './SingleFilterConverter/UserIdConverter';
import * as UserAttributeConverter from './SingleFilterConverter/UserAttributeConverter';
import * as UTurnConverter from './SingleFilterConverter/UTurnConverter';
import * as TeamCommentConverter from './SingleFilterConverter/TeamCommentConverter';

import type { QueryFormatFilter } from '../../domains/global-filters/model/QueryFormat';
import {
  QuerySubKeyEnum,
  QueryKeyEnum,
  QueryListOperandEnum,
  QueryMapOperandEnum,
} from '../../domains/global-filters/model/QueryFormat';
import type {
  IntermediateFormat,
  IntermediateFunnelFormat,
} from '../../domains/global-filters/model/IntermediateFormat';
import type {
  DeviceFilter,
  FilterType,
  GlobalFilter,
} from '../../domains/global-filters/model/FilterChain';
import { FilterKindEnum } from '../../domains/global-filters/model/FilterChain';
import type { DEVICE_TYPE } from '../../components/StatelessComponent/SingleFilter/DeviceTypeFilter/DeviceFilterType';
import type { DateRange } from '../../components/StatelessComponent/SingleFilter/DateRangeFilter/DateRangeFilterType';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const shouldReturnUndefined = (never: never): undefined => {
  return undefined;
};

// We traverse the JSON and fill up intermediateFormatFilter object
function convertOneFilterFromQueryToIntermediate(
  queryFormatFilter: QueryFormatFilter,
  level: number,
): IntermediateFormat {
  let intermediateFormatFilter: IntermediateFormat = {
    value: [],
    subKey: [],
    operation: [],
    negate: undefined,
    type: undefined,
    userAttributeKey: undefined,
  };

  const key = Object.keys(queryFormatFilter)[0];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const value: any = (queryFormatFilter as any)[key as any];

  if (
    Object.values(QueryListOperandEnum).includes(key as QueryListOperandEnum) ||
    key === QueryKeyEnum.clicked ||
    key === QueryKeyEnum.error
  ) {
    if (key === QueryKeyEnum.clicked) {
      // "Clicked" filter is a filter containing sub-filters so it follow a specific logic
      intermediateFormatFilter.type = QueryKeyEnum.clicked;
    } else if (key === QueryKeyEnum.error) {
      // "Error" filter is a filter containing sub-filters so it follow a specific logic
      intermediateFormatFilter.type = QueryKeyEnum.error;
    }

    // the value is a list
    (value as QueryFormatFilter[]).forEach((item) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const subInfo = convertOneFilterFromQueryToIntermediate(item, level + 1);
      intermediateFormatFilter = {
        value: [...intermediateFormatFilter.value, ...subInfo.value],
        operation: [...intermediateFormatFilter.operation, ...subInfo.operation],
        negate: intermediateFormatFilter.negate ?? subInfo.negate,
        type: intermediateFormatFilter.type ?? subInfo.type,
        userAttributeKey: intermediateFormatFilter.userAttributeKey ?? subInfo.userAttributeKey,
        subKey: [...intermediateFormatFilter.subKey, ...subInfo.subKey],
      };
    });
  } else if (Object.values(QueryMapOperandEnum).includes(key as QueryMapOperandEnum)) {
    // the value is another map
    if (key === QueryMapOperandEnum.NOT) {
      if (level > 1) {
        // We need to distinguish between the sub filter NOT of clicked element
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        intermediateFormatFilter.operation.push(key as any);
      } else {
        intermediateFormatFilter.negate = true;
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      intermediateFormatFilter.operation.push(key as any);
    }
    const subInfo = convertOneFilterFromQueryToIntermediate(value, level + 1);
    intermediateFormatFilter = {
      value: [...intermediateFormatFilter.value, ...subInfo.value],
      operation: [...intermediateFormatFilter.operation, ...subInfo.operation],
      negate: intermediateFormatFilter.negate ?? subInfo.negate,
      type: intermediateFormatFilter.type ?? subInfo.type,
      userAttributeKey: intermediateFormatFilter.userAttributeKey ?? subInfo.userAttributeKey,
      subKey: [...intermediateFormatFilter.subKey, ...subInfo.subKey],
    };
  } else if (Object.values(QuerySubKeyEnum).includes(key as QuerySubKeyEnum)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    intermediateFormatFilter.subKey.push(key as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    intermediateFormatFilter.value.push(value as any);
  } else {
    // the value is a leaf

    // let's find out the type
    const isUserAttribute = key.includes('user_attributes.');
    if (isUserAttribute) {
      intermediateFormatFilter.type = QueryKeyEnum.user_attributes;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      intermediateFormatFilter.userAttributeKey = key as any;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      intermediateFormatFilter.type = key as any;
    }
    if (Array.isArray(value)) {
      intermediateFormatFilter.value = [...intermediateFormatFilter.value, ...value];
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      intermediateFormatFilter.value.push(value as any);
    }
  }

  return intermediateFormatFilter;
}

function convertFunnelFilterFromQueryToIntermediate(queryFormatFilter: {
  [key in QueryKeyEnum.funnel]?: QueryFormatFilter[];
}): IntermediateFunnelFormat {
  let withDropoff = false;

  const steps: IntermediateFormat[] =
    queryFormatFilter.FUNNEL?.map((item) => {
      let itemToConvert = item;
      if (QueryMapOperandEnum.DROPOFF in item) {
        withDropoff = true;
        itemToConvert = item[QueryMapOperandEnum.DROPOFF] as QueryFormatFilter;
      }
      return convertOneFilterFromQueryToIntermediate(itemToConvert, 1);
    }) ?? [];

  const intermediateFormatFunnel: IntermediateFunnelFormat = {
    type: QueryKeyEnum.funnel,
    steps,
    withDropoff,
  };

  return intermediateFormatFunnel;
}

const convertFunnelOrFilter = (item: QueryFormatFilter) =>
  'FUNNEL' in item
    ? convertFunnelFilterFromQueryToIntermediate(item)
    : convertOneFilterFromQueryToIntermediate(item, 1);

function convertFromQueryToIntermediateFormatUnfiltered(
  queryFormatFilter: QueryFormatFilter,
): Array<IntermediateFormat | IntermediateFunnelFormat> {
  // It's an empty query
  if (Object.keys(queryFormatFilter).length === 0) {
    return [];
  }

  if ('AND' in queryFormatFilter) {
    // We have a list of filters to convert
    return queryFormatFilter.AND?.map((item) => convertFunnelOrFilter(item)) ?? [];
  }

  // We have one filter to convert
  return [convertFunnelOrFilter(queryFormatFilter)];
}

export function convertFromQueryToIntermediateFormat(
  filtersInQueryFormat: QueryFormatFilter,
): Array<IntermediateFormat | IntermediateFunnelFormat> {
  const list = convertFromQueryToIntermediateFormatUnfiltered(filtersInQueryFormat);
  // When calling the backend, we merge the local filter 'watched' with the existing filters
  // The backend sends it back to us so we should ignore it.
  return list.filter(
    (item) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      !('operation' in item) || ((item.operation as any) !== 'watched' && true),
  );
}

function convertFromIntermediateFormatToFilterType(
  filtersInIntermediateFormat: Array<IntermediateFormat | IntermediateFunnelFormat>,
): FilterType[] {
  const filterList = filtersInIntermediateFormat
    .map<FilterType | undefined>((filter) => {
      const id = generateUUID();
      if (!filter.type) {
        return undefined;
      }
      switch (filter.type) {
        case QuerySubKeyEnum.page_path:
        case QuerySubKeyEnum.selector:
        case QuerySubKeyEnum.text:
        case QueryKeyEnum.created: {
          // "created" key and all the sub keys can be ignored in that context
          return undefined;
        }
        case QueryKeyEnum.clicked: {
          return {
            id,
            kind: FilterKindEnum.Clicked,
            value: ClickedConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.error: {
          return {
            id,
            kind: FilterKindEnum.Error,
            value: ErrorConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.funnel: {
          return {
            id,
            kind: FilterKindEnum.Funnel,
            value: {
              steps: convertFromIntermediateFormatToFilterType(filter.steps),
              withDropoff: filter.withDropoff,
            },
          };
        }
        case QueryKeyEnum.new_user: {
          return {
            id,
            kind: FilterKindEnum.NewReturningUserKind,
            value: NewReturningUserConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.rageclick: {
          return {
            id,
            kind: FilterKindEnum.RageClickKind,
            value: RageClickConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.uturn: {
          return {
            id,
            kind: FilterKindEnum.UTurnKind,
            value: UTurnConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.device: {
          return {
            id,
            kind: FilterKindEnum.DeviceType,
            value: DeviceTypeConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.os: {
          return {
            id,
            kind: FilterKindEnum.OS,
            value: OperatingSystemConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.all_page_paths: {
          return {
            id,
            kind: FilterKindEnum.VisitedURL,
            value: URLConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.first_page_path: {
          return {
            id,
            kind: FilterKindEnum.LandingURL,
            value: URLConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.last_page_path: {
          return {
            id,
            kind: FilterKindEnum.ExitURL,
            value: URLConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.referrer: {
          return {
            id,
            kind: FilterKindEnum.ReferrerURL,
            value: URLConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.browser: {
          return {
            id,
            kind: FilterKindEnum.Browser,
            value: BrowserConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.incoming_feedback: {
          return {
            id,
            kind: FilterKindEnum.IncomingFeedback,
            value: FeedbackConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.nps: {
          return {
            id,
            kind: FilterKindEnum.NPS,
            value: NPSConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.visitor_uuid: {
          return {
            id,
            kind: FilterKindEnum.UserID,
            value: UserIdConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.page_count: {
          return {
            id,
            kind: FilterKindEnum.PageCount,
            value: NumberConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.duration: {
          return {
            id,
            kind: FilterKindEnum.SessionDuration,
            value: SessionDurationConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.country_code: {
          return {
            id,
            kind: FilterKindEnum.Country,
            value: LargeStringListConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.javascript_tag_names: {
          return {
            id,
            kind: FilterKindEnum.Event,
            value: LargeStringListConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.experiments: {
          if (startsWith(filter.value[0] as string, ExperimentConverter.GOOGLE_OPTIMIZE_PREFIX)) {
            return {
              id,
              kind: FilterKindEnum.GoogleOptimize,
              value: ExperimentConverter.fromIntermediateToUiFormat(filter),
            };
          }
          if (startsWith(filter.value[0] as string, ExperimentConverter.OPTIMIZELY_PREFIX)) {
            return {
              id,
              kind: FilterKindEnum.Optimizely,
              value: ExperimentConverter.fromIntermediateToUiFormat(filter),
            };
          }
          if (startsWith(filter.value[0] as string, ExperimentConverter.UNBOUNCE_PREFIX)) {
            return {
              id,
              kind: FilterKindEnum.Unbounce,
              value: ExperimentConverter.fromIntermediateToUiFormat(filter),
            };
          }
          return undefined;
        }
        case QueryKeyEnum.user_attributes: {
          return {
            id,
            kind: FilterKindEnum.UserAttribute,
            value: UserAttributeConverter.fromIntermediateToUiFormat(filter),
          };
        }
        case QueryKeyEnum.commented: {
          return {
            id,
            kind: FilterKindEnum.TeamComment,
            value: TeamCommentConverter.fromIntermediateToUiFormat(filter),
          };
        }
        default: {
          return shouldReturnUndefined(filter.type);
        }
      }
    })
    .filter((filter): filter is FilterType => filter !== undefined);

  return filterList;
}

export function convertFromQueryToNewUiFormat(
  filtersInQueryFormat: QueryFormatFilter,
): GlobalFilter {
  const filtersInIntermediateFormat = convertFromQueryToIntermediateFormat(filtersInQueryFormat);

  const intermediateDateFilter = filtersInIntermediateFormat.find(
    (filter) => filter.type === QueryKeyEnum.created,
  );
  const dateRange: DateRange | undefined = intermediateDateFilter
    ? DateRangeConverter.fromIntermediateToUiFormat(intermediateDateFilter as IntermediateFormat)
    : undefined;

  const filterList = convertFromIntermediateFormatToFilterType(filtersInIntermediateFormat);

  return dateRange
    ? {
        dateRange,
        filterList,
      }
    : { filterList };
}

export function extractDeviceType(filterList: FilterType[]): DEVICE_TYPE[] | undefined {
  const deviceFilter = filterList.find((filter) => filter.kind === FilterKindEnum.DeviceType) as
    | DeviceFilter
    | undefined;
  if (deviceFilter) {
    return deviceFilter.value;
  }
  return undefined;
}
